import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, StyleSheet, Text, View } from 'react-native';
import Svg, { Path, Rect } from 'react-native-svg';

import CheckboxIcon from '../../../assets/login/checkbox.png';
import type { InstallMethod } from '../../../services/pwa';
import { useSafeViewport } from '../../../utils/hooks/useSafeViewport';
import { IndicatorScrollView } from '../common/ScrollIndicator';
import { TouchableOpacity } from '../common/Touchable';
import { redesignPanelColors } from '../Modals/redesignPanelChrome';
import SvaraModal from '../Modals/SvaraModal';

/**
 * The "install Svara as an app" nudge, in the v2 popup style.
 *
 * Two bodies behind one frame:
 *   - `native` (Chromium on Android): one Install button that opens the browser's own
 *     dialog, a "Not now" link and, when the policy allows, a "don't show again" box.
 *   - `manual-ios`: iOS has no install API, so the body walks through the share sheet.
 *     Shown once, so there is nothing to opt out of.
 *
 * Decisions (when to show, what to remember) live in InstallNudgeHost; this is the view.
 */
type Props = {
	method: Exclude<InstallMethod, 'none'>;
	/** Show the "don't show this again" checkbox (second Android showing on). */
	offerNeverShowAgain: boolean;
	onInstall: () => void;
	/** Closed without installing; `neverShowAgain` is the checkbox, when offered. */
	onDismiss: (neverShowAgain: boolean) => void;
};

const APP_ICON = { uri: '/images/pwa/icon-192.png' };
const GLYPH = redesignPanelColors.gold;

/** The iOS share glyph: an arrow rising out of an open box. */
const ShareGlyph = () => (
	<Svg width={22} height={22} viewBox='0 0 24 24' fill='none'>
		<Path
			d='M12 15V3M7.5 7.5L12 3l4.5 4.5M8 10H5v11h14V10h-3'
			stroke={GLYPH}
			strokeWidth={2}
			strokeLinecap='round'
			strokeLinejoin='round'
		/>
	</Svg>
);

/** The "Add to Home Screen" glyph: a plus in a rounded square. */
const AddGlyph = () => (
	<Svg width={22} height={22} viewBox='0 0 24 24' fill='none'>
		<Rect x={3.5} y={3.5} width={17} height={17} rx={4} stroke={GLYPH} strokeWidth={2} />
		<Path d='M12 8v8M8 12h8' stroke={GLYPH} strokeWidth={2} strokeLinecap='round' />
	</Svg>
);

const DoneGlyph = () => (
	<Svg width={22} height={22} viewBox='0 0 24 24' fill='none'>
		<Path
			d='M5 12.5l4.5 4.5L19 7.5'
			stroke={GLYPH}
			strokeWidth={2.5}
			strokeLinecap='round'
			strokeLinejoin='round'
		/>
	</Svg>
);

const Step = ({ glyph, text }: { glyph: React.ReactNode; text: string }) => (
	<View style={styles.step}>
		<View style={styles.stepGlyph}>{glyph}</View>
		<Text style={styles.stepText}>{text}</Text>
	</View>
);

/**
 * Below this viewport height (a phone held sideways) the stacked layout no longer fits, so
 * the panel goes two-column: icon, title and text on the left, the steps or buttons on the right.
 */
const COMPACT_MAX_HEIGHT = 480;
/** The shared panel is capped at 90 % of the viewport; this is its own chrome within that. */
const PANEL_CHROME_HEIGHT = 56;

export const InstallAppModal = ({ method, offerNeverShowAgain, onInstall, onDismiss }: Props) => {
	const { t } = useTranslation();
	const { width, height } = useSafeViewport();
	const [neverShowAgain, setNeverShowAgain] = useState(false);
	const compact = height < COMPACT_MAX_HEIGHT;

	const dismiss = () => onDismiss(offerNeverShowAgain && neverShowAgain);

	const intro = (
		<View style={[styles.header, compact && styles.headerCompact]}>
			<Image
				source={APP_ICON}
				style={[styles.appIcon, compact && styles.appIconCompact]}
				resizeMode='cover'
			/>
			<Text style={[styles.title, compact && styles.titleCompact]}>
				{t('T_SITE_PWA_INSTALL_TITLE', { defaultValue: 'Play Svara as an app' })}
			</Text>
			<Text style={[styles.body, compact && styles.bodyCompact]}>
				{t('T_SITE_PWA_INSTALL_BODY', {
					defaultValue:
						'Add Svara to your home screen for full-screen play, better sound and a quicker start.',
				})}
			</Text>
		</View>
	);

	const actions =
		method === 'manual-ios' ? (
			<>
				<View style={[styles.steps, compact && styles.stepsCompact]}>
					<Step
						glyph={<ShareGlyph />}
						text={t('T_SITE_PWA_INSTALL_IOS_STEP_SHARE', {
							defaultValue: 'Tap the Share button in your browser',
						})}
					/>
					<Step
						glyph={<AddGlyph />}
						text={t('T_SITE_PWA_INSTALL_IOS_STEP_ADD', {
							defaultValue: 'Choose "Add to Home Screen"',
						})}
					/>
					<Step
						glyph={<DoneGlyph />}
						text={t('T_SITE_PWA_INSTALL_IOS_STEP_OPEN', {
							defaultValue: 'Tap Add, then open Svara from your home screen',
						})}
					/>
				</View>
				<Text style={[styles.hint, compact && styles.hintCompact]}>
					{t('T_SITE_PWA_INSTALL_LOGIN_HINT', {
						defaultValue: 'You will be asked to log in once in the installed app.',
					})}
				</Text>
				<TouchableOpacity style={styles.primaryButton} onPress={dismiss} activeOpacity={0.8}>
					<Text style={styles.primaryButtonText}>
						{t('T_SITE_PWA_INSTALL_GOT_IT', { defaultValue: 'Got it' })}
					</Text>
				</TouchableOpacity>
			</>
		) : (
			<>
				<TouchableOpacity style={styles.primaryButton} onPress={onInstall} activeOpacity={0.8}>
					<Text style={styles.primaryButtonText}>
						{t('T_SITE_PWA_INSTALL_BUTTON', { defaultValue: 'Install' })}
					</Text>
				</TouchableOpacity>
				<TouchableOpacity style={styles.secondaryButton} onPress={dismiss} activeOpacity={0.7}>
					<Text style={styles.secondaryButtonText}>
						{t('T_SITE_PWA_INSTALL_NOT_NOW', { defaultValue: 'Not now' })}
					</Text>
				</TouchableOpacity>
				{offerNeverShowAgain && (
					<TouchableOpacity
						style={styles.checkboxRow}
						onPress={() => setNeverShowAgain(value => !value)}
						activeOpacity={0.8}
						accessibilityRole='checkbox'
						accessibilityState={{ checked: neverShowAgain }}
					>
						<View style={styles.checkbox}>
							{neverShowAgain && (
								<Image source={CheckboxIcon} style={styles.checkboxIcon} resizeMode='contain' />
							)}
						</View>
						<Text style={styles.checkboxLabel}>
							{t('T_SITE_PWA_INSTALL_NEVER_SHOW', { defaultValue: "Don't show this again" })}
						</Text>
					</TouchableOpacity>
				)}
			</>
		);

	return (
		<SvaraModal
			visible
			onClose={dismiss}
			containerStyles={
				compact
					? { width: Math.min(width * 0.9, 680), maxWidth: 680 }
					: { width: Math.min(width * 0.9, 420), maxWidth: 420 }
			}
			contentWrapperStyles={compact ? styles.contentWrapperCompact : styles.contentWrapper}
		>
			{/* Whatever the viewport, the content scrolls inside the panel rather than past its edge. */}
			<IndicatorScrollView
				style={{ maxHeight: height * 0.9 - PANEL_CHROME_HEIGHT }}
				contentContainerStyle={compact ? styles.columns : undefined}
				reserveGutter={false}
			>
				{compact ? (
					<>
						<View style={styles.column}>{intro}</View>
						<View style={[styles.column, styles.columnActions]}>{actions}</View>
					</>
				) : (
					<>
						{intro}
						{actions}
					</>
				)}
			</IndicatorScrollView>
		</SvaraModal>
	);
};

export default InstallAppModal;

const styles = StyleSheet.create({
	contentWrapper: {
		alignItems: 'stretch',
		paddingHorizontal: 24,
		paddingTop: 28,
		paddingBottom: 24,
	},
	contentWrapperCompact: {
		alignItems: 'stretch',
		paddingHorizontal: 20,
		paddingTop: 16,
		paddingBottom: 16,
	},
	columns: {
		flexDirection: 'row',
		alignItems: 'flex-start',
	},
	column: {
		flex: 1,
		minWidth: 0,
	},
	columnActions: {
		marginLeft: 20,
		// The close button hangs in the panel's top-right corner (8px in, 44px tall): the right
		// column starts below it, while the left one may use the full height.
		paddingTop: 40,
	},
	header: {
		alignItems: 'center',
		marginBottom: 18,
	},
	headerCompact: {
		marginBottom: 0,
		paddingRight: 4,
	},
	appIconCompact: {
		width: 52,
		height: 52,
		borderRadius: 12,
		marginBottom: 8,
	},
	titleCompact: {
		fontSize: 19,
		marginBottom: 4,
	},
	bodyCompact: {
		fontSize: 13,
		lineHeight: 18,
	},
	stepsCompact: {
		paddingVertical: 2,
		paddingHorizontal: 10,
		marginBottom: 8,
	},
	hintCompact: {
		fontSize: 12,
		lineHeight: 16,
		marginBottom: 8,
	},
	appIcon: {
		width: 72,
		height: 72,
		borderRadius: 16,
		marginBottom: 14,
		borderWidth: 1,
		borderColor: redesignPanelColors.tileBorder,
	},
	title: {
		fontFamily: 'Sofia Sans',
		color: redesignPanelColors.title,
		fontSize: 22,
		fontWeight: '700',
		textAlign: 'center',
		marginBottom: 8,
	},
	body: {
		fontFamily: 'Sofia Sans',
		color: redesignPanelColors.white,
		fontSize: 15,
		lineHeight: 21,
		textAlign: 'center',
		opacity: 0.9,
	},
	steps: {
		backgroundColor: redesignPanelColors.tileFill,
		borderColor: redesignPanelColors.tileBorder,
		borderWidth: 1,
		borderRadius: 12,
		paddingVertical: 6,
		paddingHorizontal: 12,
		marginBottom: 12,
	},
	step: {
		flexDirection: 'row',
		alignItems: 'center',
		paddingVertical: 8,
	},
	stepGlyph: {
		width: 36,
		height: 36,
		borderRadius: 18,
		backgroundColor: redesignPanelColors.surface,
		alignItems: 'center',
		justifyContent: 'center',
		marginRight: 12,
	},
	stepText: {
		flex: 1,
		fontFamily: 'Sofia Sans',
		color: redesignPanelColors.white,
		fontSize: 15,
		lineHeight: 20,
	},
	hint: {
		fontFamily: 'Sofia Sans',
		color: redesignPanelColors.white,
		opacity: 0.65,
		fontSize: 13,
		lineHeight: 18,
		textAlign: 'center',
		marginBottom: 16,
	},
	primaryButton: {
		backgroundColor: redesignPanelColors.gold,
		borderRadius: 8,
		paddingVertical: 13,
		alignItems: 'center',
		justifyContent: 'center',
	},
	primaryButtonText: {
		fontFamily: 'Sofia Sans',
		color: '#000',
		fontSize: 16,
		fontWeight: '700',
	},
	secondaryButton: {
		paddingVertical: 12,
		alignItems: 'center',
	},
	secondaryButtonText: {
		fontFamily: 'Sofia Sans',
		color: redesignPanelColors.white,
		fontSize: 15,
		opacity: 0.8,
	},
	checkboxRow: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'center',
		paddingVertical: 4,
	},
	checkbox: {
		width: 22,
		height: 22,
		borderRadius: 5,
		borderWidth: 1.5,
		borderColor: redesignPanelColors.gold,
		alignItems: 'center',
		justifyContent: 'center',
		marginRight: 10,
	},
	checkboxIcon: {
		width: 16,
		height: 16,
	},
	checkboxLabel: {
		fontFamily: 'Sofia Sans',
		color: redesignPanelColors.white,
		fontSize: 14,
		opacity: 0.85,
	},
});
